import { Dispatch, SetStateAction, useState } from 'react';
import {
    Alert,
    AlertContent,
    Button,
    CheckboxGroup,
    Fieldset,
    Group,
    Input,
    Label,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalLayout,
    ModalTitle,
    MoneyField,
    PercentageField,
    RadioGroup,
    YearAndMonthPickerPopover,
} from '@tlx/atlas';
import { FormattedMessage, useIntl } from 'react-intl';
import { APIError, ListResponse, ResponseWrapper, showActionLogMessage } from '@tlx/astro-shared';
import { BodyDTO, EmployeeDTO, SalaryV2TransactionDTO } from '@Models/model.types';
import { dateUtil } from '@Util/date';
import { UncontrolledForm } from '@Component/Form/UncontrolledForm';
import { callApi, computeUrl, mapObjectToUrlSearchParams, MethodType } from '@Util/api';
import { mapValidationMessages } from '@Util/apiErrorUtils.ts';
import { SpinnerButton } from '@tlx/tsk-shared';
import { validateNumber } from '@Util/numberUtils';
import { APIUrls } from '@Util/urls';
import { SanitizedHTMLAndMarkdown } from '@Util/SanitizedHTMLAndMarkdown.tsx';
import { createOrdinalFromArray } from '@Util/misc.ts';
import { SWRInfiniteKeyedMutator } from 'swr/infinite';

export type WageAdjustmentDTO = {
    startDate: string;
    yearly?: string;
    hourly?: string;
    percent?: string;
    roundOff: string;
};

export function WageAdjustmentModal({
    open,
    onClose,
    employeeIds,
    mutate,
    setValidationsMap,
}: {
    open: boolean;
    onClose: () => void;
    employeeIds: number[];
    mutate: SWRInfiniteKeyedMutator<ListResponse<EmployeeDTO>[]>;
    setValidationsMap: Dispatch<SetStateAction<Map<number, string[]>>>;
}) {
    const formId = 'wageAdjustmentForm';
    const [awaitingResponse, setAwaitingResponse] = useState<boolean>(false);
    const [selectedPeriod, setSelectedPeriod] = useState<Date>(dateUtil.today());
    const [errorsToShowInModal, setErrorsToShowInModal] = useState<string[]>([]);
    const [type, setType] = useState<string>('amount');
    const intl = useIntl();
    const submitForm = async (_: HTMLFormElement, formData: { [p: string]: FormDataEntryValue }) => {
        if (!formData.yearly && !formData.hourly && !formData.percent) {
            setErrorsToShowInModal([intl.formatMessage({ id: 'validation_form_not_completed' })]);
            return;
        }
        setAwaitingResponse(true);
        const wageAdjustmentDTO: WageAdjustmentDTO = {
            startDate: dateUtil.formatDate(selectedPeriod, 'no'),
            yearly: formData.yearly as string,
            hourly: formData.hourly as string,
            percent: formData.percent as string,
            roundOff: (formData.roundOff === 'on').toString(),
        };

        const bodyDTO: BodyDTO = {
            employeeIds: employeeIds.join(','),
        };

        const { response, error } = await callApi<ResponseWrapper<SalaryV2TransactionDTO>>(
            computeUrl(APIUrls.WAGE_ADJUSTMENT, mapObjectToUrlSearchParams(wageAdjustmentDTO)),
            MethodType.POST,
            JSON.stringify(bodyDTO),
        );
        setAwaitingResponse(false);
        if (error) {
            if (error instanceof APIError) {
                mapValidationMessages(
                    error.details.validationMessages,
                    setValidationsMap,
                    setErrorsToShowInModal,
                    intl.formatMessage({ id: 'validation_unexpected_error' }),
                    onClose,
                );
            }
        }
        if (response) {
            onClose();
            mutate();
            showActionLogMessage({
                pageName: intl.formatMessage({
                    id: 'heading_employee_overview',
                }),
                date: new Date(),
                message: intl.formatMessage(
                    {
                        id:
                            employeeIds.length === 1
                                ? 'tsk_wage_adjustment_success_singular'
                                : 'tsk_wage_adjustment_success_plural',
                    },
                    createOrdinalFromArray([employeeIds.length]),
                ),
            });
        }
    };

    return (
        <Modal
            open={open}
            onClose={() => {
                if (!awaitingResponse) {
                    // to avoid user "cancelling" the dialog by clicking outside it
                    onClose();
                }
            }}
        >
            <ModalLayout>
                {!awaitingResponse && <ModalCloseButton />}
                <ModalTitle>
                    <FormattedMessage id={'text_adjust_wages'} />
                </ModalTitle>
                <ModalContent className={'tsk-width-500'}>
                    {errorsToShowInModal.length > 0 && (
                        <Alert variant="error" className={'atl-mb-24'}>
                            <AlertContent>
                                <SanitizedHTMLAndMarkdown content={errorsToShowInModal.join('\n')} />
                            </AlertContent>
                        </Alert>
                    )}
                    <UncontrolledForm submitForm={submitForm} id={formId}>
                        <Group className={'tsk-flex-half atl-mb-24'}>
                            <Label>
                                <FormattedMessage id={'text_period'} />
                            </Label>
                            <YearAndMonthPickerPopover
                                className={'atl-w-full'}
                                selectedDate={selectedPeriod}
                                onChange={(newDate) => {
                                    setSelectedPeriod(newDate);
                                }}
                                pastYears={1}
                                futureYears={1}
                            />
                        </Group>
                        <Fieldset className={'atl-mb-24'} onChange={() => setErrorsToShowInModal([])}>
                            <RadioGroup>
                                <Input
                                    type={'radio'}
                                    data-testid={'type'}
                                    checked={type === 'amount'}
                                    onChange={() => setType('amount')}
                                />
                                <FormattedMessage id={'text_amount'} />
                            </RadioGroup>
                            <RadioGroup>
                                <Input
                                    type={'radio'}
                                    data-testid={'type'}
                                    checked={type === 'percent'}
                                    onChange={() => setType('percent')}
                                />
                                <FormattedMessage id={'text_percent'} />
                            </RadioGroup>
                        </Fieldset>
                        {type === 'amount' && (
                            <div className={'atl-flex atl-gap-x-16 atl-mb-24'}>
                                <MoneyField
                                    label={intl.formatMessage({
                                        id: 'th_change_yearly_wages',
                                    })}
                                    data-testid={'yearly-input'}
                                    className={'atl-w-full tsk-flex-half'}
                                    name={'yearly'}
                                    validate={(value) => validateNumber(intl, value)}
                                />
                                <MoneyField
                                    label={intl.formatMessage({
                                        id: 'text_change_hourly_wage',
                                    })}
                                    data-testid={'hourly-input'}
                                    className={'atl-w-full tsk-flex-half'}
                                    name={'hourly'}
                                    validate={(value) => validateNumber(intl, value)}
                                />
                            </div>
                        )}
                        {type === 'percent' && (
                            <PercentageField
                                className={'atl-w-full tsk-flex-half atl-mb-24'}
                                data-testid={'percent-input'}
                                name={'percent'}
                                label={intl.formatMessage({
                                    id: 'text_percentage',
                                })}
                                isRequired
                            />
                        )}
                        <div className={'atl-flex atl-gap-x-16'}>
                            <CheckboxGroup>
                                <Input
                                    type={'checkbox'}
                                    data-testid={'round-off-checkbox'}
                                    name={'roundOff'}
                                    defaultChecked={true}
                                />
                                <FormattedMessage id={'text_roundoff_wage'} />
                            </CheckboxGroup>
                        </div>
                    </UncontrolledForm>
                </ModalContent>
                <ModalFooter className={'atl-flex atl-gap-8'}>
                    <SpinnerButton
                        className="atl-button--primary"
                        data-testid="wage-adjustment-run-button"
                        type={'submit'}
                        form={formId}
                        disabled={awaitingResponse}
                        isLoading={awaitingResponse}
                    >
                        <FormattedMessage id={'button_save'} />
                    </SpinnerButton>
                    <Button
                        variant={'secondary'}
                        onClick={onClose}
                        data-testid="wage-adjustment-modal-cancel-button"
                        disabled={awaitingResponse}
                    >
                        <FormattedMessage id={'text_cancel'} />
                    </Button>
                </ModalFooter>
            </ModalLayout>
        </Modal>
    );
}
